// src/screens/academique/MesResultats.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Paper,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as EmojiEventsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarTodayIcon,
  Star as StarIcon
} from '@mui/icons-material';
import FormationMainContainer from '../../components/FormationMainContainer';
import LocalDataService from '../../services/LocalDataService';

const MesResultats = () => {
  const [loading, setLoading] = useState(true);
  const [qcmResults, setQcmResults] = useState([]);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les données depuis le service local
        const userData = LocalDataService.getUser();
        const qcmData = LocalDataService.getQcmResults(userData.data);
        
        setStudent(userData.dataSpecial);
        setQcmResults(qcmData);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const avgScore = qcmResults?.length 
    ? Math.round(qcmResults.reduce((a, r) => a + (r.score || 0), 0) / qcmResults.length) 
    : 0;
  const bestScore = qcmResults?.length 
    ? Math.max(...qcmResults.map(r => r.score || 0)) 
    : 0;
  const passedCount = qcmResults?.filter(r => r.score >= 50).length || 0;
  
  const formatDate = (date) => {
    if (!date) return 'Date inconnue';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 75) return '#28A745';
    if (score >= 50) return '#FFC107';
    return '#DC3545';
  };

  const getScoreBgColor = (score) => {
    if (score >= 75) return 'rgba(40,167,69,0.08)';
    if (score >= 50) return 'rgba(255,193,7,0.08)';
    return 'rgba(220,53,69,0.08)';
  };

  if (loading) {
    return (
      <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Chargement de vos résultats...
            </Typography>
          </Stack>
        </Box>
      </FormationMainContainer>
    );
  }

  if (!qcmResults || qcmResults.length === 0) {
    return (
      <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Paper sx={{ textAlign: 'center', p: 8, borderRadius: '16px', bgcolor: '#F9FAFB', maxWidth: 500, mx: 'auto' }}>
            <BarChartIcon sx={{ fontSize: 64, color: '#9E9E9E', opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun résultat QCM
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Commencez par faire des QCM dans la section "Mes cours"
            </Typography>
          </Paper>
        </Box>
      </FormationMainContainer>
    );
  }

  return (
    <FormationMainContainer maxWidth="xl" sx={{ py: 3 }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="800" gutterBottom display="flex" alignItems="center" gap={1}>
          <BarChartIcon sx={{ color: '#1976D2', fontSize: 32 }} />
          Mes Résultats
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Historique de vos scores QCM
        </Typography>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Score moyen */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: '100%',
              borderRadius: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  bgcolor: 'rgba(0,86,164,0.1)',
                  color: '#0056A4'
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 35 }} />
              </Box>
              <Typography variant="h3" fontWeight="800" color="#0056A4" gutterBottom>
                {avgScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Score moyen
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Meilleur score */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: '100%',
              borderRadius: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  bgcolor: 'rgba(40,167,69,0.1)',
                  color: '#28A745'
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 35 }} />
              </Box>
              <Typography variant="h3" fontWeight="800" color="#28A745" gutterBottom>
                {bestScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Meilleur score
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* QCM réussis */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: '100%',
              borderRadius: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  bgcolor: 'rgba(253,126,20,0.1)',
                  color: '#FD7E14'
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 35 }} />
              </Box>
              <Typography variant="h3" fontWeight="800" color="#FD7E14" gutterBottom>
                {passedCount}/{qcmResults.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                QCM réussis (≥50%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Liste des résultats */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Historique détaillé
      </Typography>

      <Stack spacing={2}>
        {qcmResults.map((result, index) => {
          const scoreColor = getScoreColor(result.score);
          const scoreBgColor = getScoreBgColor(result.score);
          
          return (
            <Card
              key={index}
              sx={{
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                  boxShadow: 4,
                  borderColor: scoreColor
                }
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* Score circulaire */}
                  <Grid item xs={12} sm={2} md={1.5}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `3px solid ${scoreColor}`,
                        bgcolor: scoreBgColor,
                        color: scoreColor,
                        mx: { xs: 'auto', sm: 0 }
                      }}
                    >
                      <Typography variant="h6" fontWeight="800">
                        {result.score}%
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Informations du cours */}
                  <Grid item xs={12} sm={5} md={5.5}>
                    <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                      {result.courseTitle || result.course_title || `QCM ${index + 1}`}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarTodayIcon sx={{ fontSize: 14, color: '#9E9E9E' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(result.date)}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* Barre de progression */}
                  <Grid item xs={12} sm={3} md={3.5}>
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={result.score}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#E9ECEF',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: scoreColor
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Progression
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Points */}
                  <Grid item xs={12} sm={2} md={1.5}>
                    <Box textAlign={{ xs: 'left', sm: 'right' }}>
                      <Typography variant="h6" fontWeight="700" color="#0056A4">
                        {result.earned_points}/{result.total_points}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        points
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Badge de performance */}
      {avgScore >= 75 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Chip
            icon={<StarIcon />}
            label="Excellent travail ! Continuez ainsi"
            sx={{
              bgcolor: '#28A745',
              color: '#FFFFFF',
              fontWeight: 600,
              p: 1,
              '& .MuiChip-icon': { color: '#FFFFFF' }
            }}
          />
        </Box>
      )}
    </FormationMainContainer>
  );
};

export default MesResultats;